import { Button } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { selectSessionEndedReason } from "../../store/reducers/sessionSlice";
import { loginUrlWithReturn } from "../../utils/login-redirect";
import { IDLE_LOCK_STORAGE_KEY } from "../../utils/pin-lock-utils";

export const SESSION_ENDED_REDIRECT_MS = 4_000;

const MESSAGE_KEYS = {
  PIN_CREATE_REAUTH_REQUIRED: "pinLock.sessionEnded.reauthRequired",
  SESSION_REUSE_DETECTED: "pinLock.sessionEnded.reuseDetected",
} as const;

/**
 * The server ended the session for security reasons (MED-1620): a PIN can
 * only be created shortly after a password login, and a replayed session
 * token closes every session. Tell the user why, then go to the login page.
 */
export const SessionEndedNotice: React.FC = () => {
  const { t } = useTranslation();
  const reason = useSelector(selectSessionEndedReason);

  const goToLogin = useCallback(() => {
    try {
      localStorage.removeItem(IDLE_LOCK_STORAGE_KEY);
    } catch {
      // Storage unavailable: nothing to clear.
    }
    window.location.href = loginUrlWithReturn();
  }, []);

  useEffect(() => {
    if (!reason) return;
    const timer = setTimeout(goToLogin, SESSION_ENDED_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [reason, goToLogin]);

  if (!reason) return null;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-gray-900/50 px-4"
      data-testid="session-ended-notice"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-ended-message"
        className="w-full max-w-sm rounded-2xl bg-white p-8 text-center shadow-xl"
      >
        <p id="session-ended-message" className="mb-6 text-gray-900">
          {t(MESSAGE_KEYS[reason])}
        </p>
        <Button variant="ews-primary" fullWidth onClick={goToLogin}>
          {t("pinLock.sessionEnded.login")}
        </Button>
      </div>
    </div>
  );
};
